import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SITE, IMAGES } from "@/lib/site";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Packages", href: "#packages" },
  { label: "Availability", href: "#availability" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-gray-100 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12 h-16 md:h-20 flex items-center justify-between">
        <a href="#top" data-testid="brand-logo" className="flex items-center group">
          <img
            src={IMAGES.logo}
            alt="Vinodha Diagnostics — imaging at its best"
            className="h-9 md:h-11 w-auto"
            width="160"
            height="48"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="px-4 py-2 text-sm font-semibold text-[#0B2545]/80 hover:text-[#0369A1] rounded-full hover:bg-[#F0F9FF] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${SITE.phoneRaw}`}
            data-testid="nav-call-btn"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B2545] hover:text-[#0369A1]"
          >
            <Phone className="w-4 h-4" /> {SITE.phone}
          </a>
          <Button
            data-testid="nav-book-btn"
            onClick={() => document.getElementById("appointment")?.scrollIntoView({ behavior: "smooth" })}
            className="rounded-full bg-[#0369A1] hover:bg-[#0284C7] text-white px-6 h-10 font-semibold"
          >
            Book Appointment
          </Button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden p-2 rounded-lg text-[#0B2545]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-gray-100 bg-white" data-testid="nav-mobile-menu">
          <div className="px-5 py-4 flex flex-col gap-1">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="px-3 py-3 rounded-lg font-semibold text-[#0B2545] hover:bg-[#F0F9FF]"
              >
                {l.label}
              </a>
            ))}
            <div className="flex gap-2 mt-2">
              <a
                href={`tel:${SITE.phoneRaw}`}
                className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-full border-2 border-[#0B2545] text-[#0B2545] font-semibold"
              >
                <Phone className="w-4 h-4" /> Call
              </a>
              <Button
                onClick={() => {
                  setOpen(false);
                  document.getElementById("appointment")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="flex-1 rounded-full bg-[#0369A1] hover:bg-[#0284C7] text-white h-11 font-semibold"
              >
                Book Now
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
